import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageLayout } from '../../components';
import { ErrorMap } from '../../components/alerts/errors/error-map';
import { CONSTANTS } from '../../constants';
import { defaultBrandId, useBrandContext } from '../../contexts/BrandProvider';
import {
    GetSsoRedirectPathQuery,
    type SsoRedirectResponse,
} from '../../queries/get-redirect-sso-path.query';

const makeParams = (parts: Array<string | null>) =>
    parts.filter(Boolean).join('&');

const SsoRedirect = () => {
    const [searchParams] = useSearchParams();
    const [acceptLoginInitiated, setAcceptLoginInitiated] =
        useState<boolean>(false);
    const loginChallenge = searchParams.get('login_challenge');
    const session = searchParams.get('session');
    const auth = searchParams.get('auth') ?? CONSTANTS.defaultAuth;
    const { setBrand } = useBrandContext();

    const Error = ErrorMap['500_GenericError'];

    if (session && loginChallenge) {
        // Redirect to accept-login api only if both login_challenge and session exists
        if (!acceptLoginInitiated) {
            setAcceptLoginInitiated(true);
            window.location.replace(
                new URL(
                    `${
                        import.meta.env.VITE_SSO_LOGIN_API_URL
                    }/sso/accept-login?login_challenge=${loginChallenge}&session_id=${session}&auth_method=${auth}`
                )
            );
        }
    }

    // call initiate login api only if login_challenge is passed and session does not exist
    const { data: redirectPathResponse, error: ssoError } = useQuery<{
        ssoRedirectResponse: SsoRedirectResponse;
    }>(GetSsoRedirectPathQuery, {
        errorPolicy: 'all',
        skip: !!session || !loginChallenge, // skip if session exists or login challenge does not exist
        variables: {
            loginChallenge,
        },
    });

    if (redirectPathResponse && redirectPathResponse.ssoRedirectResponse) {
        const {
            redirectUri,
            patientId,
            flow,
            mustReauthenticate,
            brandId,
            drxId,
        } = redirectPathResponse.ssoRedirectResponse;
        const callBackUrl = window.location.href;
        const fixedParams = [
            patientId?.length ? `user=${patientId}` : null,
            brandId ? `brandId=${brandId}` : null,
            drxId ? `drxId=${drxId}` : null,
            `callback=${callBackUrl}`,
        ];

        setBrand(brandId ?? defaultBrandId);
        let url;
        switch (flow) {
            case 'employer':
                url = `${redirectUri}?${makeParams(['showmember=true', ...fixedParams])}}`;
                break;
            case 'signIn':
                if (mustReauthenticate) {
                    url = `${redirectUri}?${makeParams(fixedParams)}`;
                } else {
                    url = `${redirectUri}?${makeParams(['showback=true', ...fixedParams])}`;
                }
                break;
            case 'createAccount':
                url = `${redirectUri}?${makeParams(['showback=true', ...fixedParams])}`;
                break;
            case 'updateProfile':
                url = `${redirectUri}?login_challenge=${loginChallenge}&callback=${callBackUrl}`;
                break;
            default:
                url = `${redirectUri}?${makeParams(fixedParams)}`;
        }
        window.location.replace(new URL(url));
    }

    // TODO: Display more helpful error message in case of failure
    const hasError = !loginChallenge || ssoError;

    return (
        <PageLayout
            loadingOverlay={!hasError}
            header={hasError && Error && <Error />}
            hideLogo
        />
    );
};

export default SsoRedirect;
